import React, { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layer } from "react-mapbox-gl";

import { palette } from "../../theme/palette";
import { BOUNDARIES_ID_BY_LEVEL } from "../../consts/sources";
import { LAYERS, OUTER, SECTOR, POSTCODE } from "../../consts/layers";
import { LOCATIONS_PLAN_TYPE, PICKUP_PLAN_TYPE } from "../../consts/planTypes";

import {
  getMatrixProfileType,
  heatmapViewSelector,
  isEditOneModeSelector,
  overlayDetailsSelector,
} from "../../reducers/matrixProfile/selectors";
import { selectedDepotFeaturesSelector } from "../../reducers/depot/selectors";
import {
  zoomLevelSelector,
  streetViewSelector,
} from "../../reducers/map/selectors";
import { setPopupDetails } from "../../reducers/map/";
import { selectedRouteSelector } from "../../reducers/ownApp/selectors";

import { Postcode } from "../../utils/postcode";

import { RouteLabelPopUp } from "./RouteLabelLayer/RouteLabelPopUp";
import { PickupLabelPopUp } from "./RouteLabelLayer/PickupLabelPopUp";

export const DepotBoundary = memo(({ map }) => {
  const dispatch = useDispatch();
  const sources = useSelector((state) => state.sources);
  const selectedDepotFeatures = useSelector(selectedDepotFeaturesSelector);
  const selectedRoute = useSelector(selectedRouteSelector);
  const isHeatmapOn = useSelector(heatmapViewSelector);
  const planType = useSelector(getMatrixProfileType);
  const streetView = useSelector(streetViewSelector);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const overlayDetails = useSelector(overlayDetailsSelector);
  const zoomLevel = useSelector(zoomLevelSelector);
  const isEditWithOverlay = isEditOneMode && !!overlayDetails;

  const setPopupDetailsFromCords = (cords) => {
    if (!cords?.point || planType === LOCATIONS_PLAN_TYPE) return;
    const features = map.queryRenderedFeatures(cords.point, {
      layers: [LAYERS[zoomLevel].BOUNDARIES.NAME],
    });

    if (!features.length) return;
    const { routeCode, root: postcode } = features[0].state;
    dispatch(
      setPopupDetails({
        point: cords.point,
        coordinates: [cords.lngLat.lng, cords.lngLat.lat],
        routes: [routeCode],
        postcode,
        level: Postcode.getLevel(postcode),
      })
    );
  };

  const getFillOpacity = () => {
    if (streetView || planType === LOCATIONS_PLAN_TYPE) return 0.2;
    else if (selectedRoute)
      return [
        "case",
        ["==", ["feature-state", "routeCode"], selectedRoute.routeCode],
        0.8,
        0.2,
      ];
    else return 0.8;
  };

  const getLevelPaint = (level) => {
    if (level !== zoomLevel)
      return {
        "fill-color": "transparent",
        "fill-opacity": 0,
        "fill-outline-color": "transparent",
      };
    else
      return {
        "fill-color": isHeatmapOn
          ? [
              "case",
              ["==", ["feature-state", "totalStops"], null],
              "#ffffff",
              ["==", ["feature-state", "totalStops"], 0],
              "#ffffff",
              ["<=", ["feature-state", "totalStops"], 50],
              palette.heatmap[0],
              ["<=", ["feature-state", "totalStops"], 100],
              palette.heatmap[1],
              ["<=", ["feature-state", "totalStops"], 150],
              palette.heatmap[2],
              ["<=", ["feature-state", "totalStops"], 200],
              palette.heatmap[3],
              ["<=", ["feature-state", "totalStops"], 250],
              palette.heatmap[4],
              [">", ["feature-state", "totalStops"], 250],
              palette.heatmap[5],
              "#ffffff",
            ]
          : ["coalesce", ["feature-state", "colour"], "#ffffff"],
        "fill-opacity": getFillOpacity(),
        "fill-outline-color": "#000",
      };
  };

  const getLayerProps = (level) => ({
    filter: [
      "in",
      ["id"],
      ["literal", selectedDepotFeatures[level].map((i) => i.id)],
    ],
    id: LAYERS[level].BOUNDARIES.NAME,
    sourceId: BOUNDARIES_ID_BY_LEVEL[level],
    sourceLayer: LAYERS[level].BOUNDARIES.SOURCE_LAYER,
    type: "fill",
    paint: getLevelPaint(level),
    onMouseLeave: (e) => dispatch(setPopupDetails(null)),
    onMouseMove: (e) =>
      isEditWithOverlay && e?.point && setPopupDetailsFromCords(e),
  });

  return map ? (
    <>
      {[OUTER, SECTOR, POSTCODE].map(
        (level) =>
          sources[BOUNDARIES_ID_BY_LEVEL[level]] &&
          selectedDepotFeatures[level].length && (
            <Layer key={level} {...getLayerProps(level)} />
          )
      )}
      {planType !== PICKUP_PLAN_TYPE ? (
        <RouteLabelPopUp setPopupDetailsFromCords={setPopupDetailsFromCords} />
      ) : (
        <PickupLabelPopUp setPopupDetailsFromCords={setPopupDetailsFromCords} />
      )}
    </>
  ) : null;
});
